/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import withTheme from 'theme/withTheme';
import stylesGlobal from 'theme/facility-globals.scss';
import { connect } from 'react-redux';
import { compose } from 'utils/react';
import { Section } from 'components';
import { Field } from 'redux-form';
import cx from 'classnames';
import { getCompanyId } from 'store/modules/company';
import { section, stylesModal } from 'components/sections/decorators';
import { BUILDER } from 'shared/abilities';
import { LESS_WHITESPACE, MORE_WHITESPACE, MOBILE_STACKED, MOBILE_CAROUSEL } from './constants';
import CTANumCols from './CTANumCols';
import CTANumItems from './CTANumItems';
import CTAWhiteSpace from './CTAWhiteSpace';
import CTAMobileBehavior from './CTAMobileBehavior';
import CTACarousel from './CTACarousel';
import CallToAction from './CTA';
import styles from './CTA.scss';

const ctaIpsum = 'Id enim volumus, id contendimus, ut officii fructus sit ipsum officium.';
const padding = {};
padding[LESS_WHITESPACE] = 0;
padding[MORE_WHITESPACE] = 150;

const CTACustom = ({
  children,
  displayContent,
  inlineStyle,
  isBuilderView,
  mobileBehavior,
  numCols,
  numItems,
  sectionStyle = {},
  theme: { fonts },
  whitespace,
}) => {
  const callouts = Array.from({ length: numItems }, (_, index) => index).map(index => ({
    image: children[0 + index],
    header: children[60 + index],
    content: displayContent ? children[120 + index] : '',
    fonts,
  }));

  const displayCarousel = mobileBehavior === MOBILE_CAROUSEL;

  const renderCTA = (props, index) => (
    <CallToAction key={`action-${index}`} numCols={numCols} {...props} />
  );

  const Carousel = React.Children.toArray(children)[180];

  const gridCTA = (
    <Section
      className={cx(stylesGlobal.section, styles.grid, (displayCarousel && styles.hideOnMobile))}
      Carousel={Carousel}
      {...sectionStyle}
      inlineStyle={inlineStyle}
    >
      <div
        className={styles.container}
        style={{ paddingRight: padding[whitespace], paddingLeft: padding[whitespace] }}
      >
        {callouts.map(renderCTA)}
      </div>
    </Section>
  );

  const carouselCTA = (
    <CTACarousel callouts={callouts} numItems={numItems} />
  );


  return (
    <React.Fragment>
      {gridCTA}
      {!isBuilderView && displayCarousel && carouselCTA}
    </React.Fragment>
  );
};

CTACustom.propTypes = {
  children: PropTypes.node.isRequired,
  companyId: PropTypes.string.isRequired,
  displayContent: PropTypes.bool,
  inlineStyle: PropTypes.object,
  isBuilderView: PropTypes.bool,
  mobileBehavior: PropTypes.oneOf([MOBILE_STACKED, MOBILE_CAROUSEL]),
  numCols: PropTypes.string,
  numItems: PropTypes.string,
  sectionStyle: PropTypes.object,
  theme: PropTypes.object.isRequired,
  whitespace: PropTypes.string,
};

CTACustom.defaultProps = {
  displayContent: true,
  isBuilderView: undefined,
  mobileBehavior: MOBILE_STACKED,
  numCols: '3',
  numItems: '6',
  whitespace: LESS_WHITESPACE,
};

const component = compose(
  connect(state => ({
    companyId: getCompanyId(state),
  })),
  stylesModal(
    props => ({
      title: 'Call to Action: Custom Editor',
      initialValues: {
        displayContent: props.displayContent,
        mobileBehavior: props.mobileBehavior,
        numCols: props.numCols,
        numItems: props.numItems,
        whitespace: props.whitespace,
      },
    }),
    [
      {
        name: 'Items and Columns',
        children: (
          <div className={styles.options}>
            <Field name="numItems" component={CTANumItems} />
            <Field name="numCols" component={CTANumCols} />
          </div>
        ),
      },
      {
        name: 'Mobile Behavior',
        children: (
          <div className={styles.options}>
            <Field name="mobileBehavior" component={CTAMobileBehavior} />
          </div>
        ),
      },
      {
        name: 'White Space',
        children: (
          <div className={styles.options}>
            <Field name="whitespace" component={CTAWhiteSpace} />
          </div>
        ),
      },
    ],
  ),
  section(
    {
      name: 'Call to Action: Custom',
      category: 'Call to Action',
      defaultPayload: {
        numCols: '3',
        numItems: '6',
        mobileBehavior: MOBILE_CAROUSEL,
        whitespace: LESS_WHITESPACE,
      },
    },
    {
      subsections: [
        ...[...Array(60)].map(() => ({ accepts: ['Image'] })),
        ...[...Array(60)].map(() => ({ accepts: ['HeaderText'] })),
        ...[...Array(60)].map(() => ({ accepts: ['ContentBox'] })),
        { accepts: ['Carousel'] },
      ],
      defaults: [
        ...[...Array(60)].map(() => [
          'Image',
          {
            image: { file: '/img/image-widget.jpg', alt: 'Call to action' },
          },
        ]),
        ...[...Array(60)].map(() => [
          'HeaderText',
          {
            rawContentState: {
              blocks: [
                {
                  key: 'db2mm',
                  text: 'Solutions',
                  type: 'header-four',
                  depth: 0,
                  inlineStyleRanges: [],
                  entityRanges: [],
                },
              ],
              entityMap: {},
            },
          },
        ]),
        ...[...Array(60)].map(() => [
          'ContentBox',
          {
            rawContentState: {
              blocks: [
                {
                  key: 'db2mm',
                  text: ctaIpsum,
                  type: 'unstyled',
                  depth: 0,
                  inlineStyleRanges: [],
                  entityRanges: [],
                },
              ],
              entityMap: {},
            },
          },
        ]),
        ['Carousel'],
      ],
      menu: { items: ['edit', 'delete'] },
    },
  ),
  withTheme,
)(CTACustom);

component.authorization = { action: 'manage', subject: BUILDER };

export default component;
